"use client";

import { createApi } from "@reduxjs/toolkit/query/react";
import qs from "qs";

import baseQuery from "@/libs/shared/api/baseQuery";

import type { DashboardNoteModel } from "./dashboard.types";

const tagTypes = [
  "Note" as const,
  "Activities" as const,
  "ActivitiesSummary" as const,
  "Deadlines" as const,
  "DeadlinesCounters" as const,
  "Referral" as const,
  "Events" as const,
  "Subscription" as const,
  "AssignmentMedia" as const,
  "WorkoutCustomersPerDay" as const,
];

export const invalidateDashboard = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(dashboardApi.util.invalidateTags([tag]));
      });
    });
  };
};

// Define a service using a base URL and expected endpoints
export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getNote: builder.query({
      query: () => ({
        url: `pt/notes`,
      }),
      providesTags: ["Note"],
    }),
    getActivities: builder.query({
      query: ({
        perPage,
        page,
        name,
        period,
        orderBy,
        orderByDirection,
      }: {
        perPage: string;
        page: string;
        name: string;
        period: string;
        orderBy: string;
        orderByDirection: string;
      }) => ({
        url: `pt/activities?per_page=${perPage}&page=${page}&name=${name}&period=${period}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      providesTags: ["Activities"],
    }),
    getActivitiesSummary: builder.query({
      query: () => ({
        url: `pt/activities`,
      }),
      providesTags: ["ActivitiesSummary"],
    }),
    getDeadlines: builder.query({
      query: ({
        perPage,
        page,
        expired,
        instanceType,
        name,
        period,
        orderBy,
        orderByDirection,
      }: {
        perPage: string;
        page: string;
        expired: string;
        instanceType: string;
        name: string;
        period: string;
        orderBy: string;
        orderByDirection: string;
      }) => ({
        url: `pt/deadlines?per_page=${perPage}&page=${page}&expired=${expired}&instance_type=${instanceType}&name=${name}&period=${period}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      providesTags: ["Deadlines"],
    }),
    getDeadlinesCounters: builder.query({
      query: () => ({
        url: `pt/deadlines/counters?expired=0`,
      }),
      providesTags: ["DeadlinesCounters"],
    }),
    getReferral: builder.query({
      query: () => ({
        url: `pt/account/referral`,
      }),
      providesTags: ["Referral"],
    }),
    getEvent: builder.query({
      query: ({
        startDate,
        endDate,
      }: {
        startDate: string;
        endDate: string;
      }) => ({
        url: `get_calendar_events?startDate=${startDate}&endDate=${endDate}`,
      }),
      providesTags: ["Events"],
    }),
    getStatisticsSummary: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
        });
        return {
          url: `pt/statistics/summary?${searchString}`,
        };
      },
    }),
    updateNote: builder.mutation({
      query: ({ updatedNote }: { updatedNote: DashboardNoteModel }) => ({
        url: `pt/notes`,
        method: "POST",
        body: updatedNote,
      }),
      onQueryStarted: invalidateDashboard(["Note"]),
    }),
    updateActivity: builder.mutation({
      query: ({ id, read }: { id: number; read: boolean }) => ({
        url: `pt/activities/${id}?read=${Number(read)}`,
        method: "PATCH",
      }),
      onQueryStarted: invalidateDashboard(["Activities"]),
    }),
    getWorkoutCustomersPerDay: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
        });
        return {
          url: `pt/statistics/workout_customers_per_day?${searchString}`,
        };
      },
      providesTags: ["WorkoutCustomersPerDay"],
    }),
    getWorkoutsSummedCustomers: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
          limit: data?.limit || 3,
        });
        return {
          url: `pt/statistics/workouts_summed_customers?${searchString}`,
        };
      },
    }),
    getWorkoutsSummedCustomersPaginated: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          page: data?.page || 1,
          per_page: data?.per_page || 10,
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
          period: data?.period || undefined,
          order_by: data?.order_by || undefined,
          order_by_direction: data?.order_by_direction || undefined,
        });
        return {
          url: `/pt/statistics/workouts_summed_customers_paginated?${searchString}`,
        };
      },
      transformResponse: (response: any) => {
        return {
          ...response,
          data: response.data.map((item: any, index: number) => ({
            ...item,
            id: index,
          })),
        };
      },
    }),
    getWorkoutsSatisfactionTotals: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
        });
        return {
          url: `pt/statistics/workouts_satisfaction_totals?${searchString}`,
        };
      },
    }),
    getUsersWorkoutsFeedbackList: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
          limit: data?.limit || 3,
        });
        return {
          url: `pt/statistics/users_workouts_feedback_list?${searchString}`,
        };
      },
    }),
    getUsersWorkoutsFeedbackPaginated: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          page: data?.page || 1,
          per_page: data?.per_page || 10,
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
          satisfaction: data?.satisfaction || undefined,
          order_by: data?.order_by || undefined,
          order_by_direction: data?.order_by_direction || undefined,
        });
        return {
          url: `pt/statistics/users_workouts_feedback_paginated?${searchString}`,
        };
      },
      transformResponse: (response: any) => {
        return {
          ...response,
          data: response.data.map((item: any, index: number) => ({
            ...item,
            id: index,
          })),
        };
      },
    }),
    getVods: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          page: data?.page || 1,
          per_page: data?.per_page || 10,
          order_by: data?.order_by || undefined,
          order_by_direction: data?.order_by_direction || undefined,
        });
        return {
          url: `pt/statistics/vods_paginated?${searchString}`,
        };
      },
    }),
    getVodsPaginated: builder.query({
      query: (data) => {
        const searchString = qs.stringify({
          page: data?.page || 1,
          per_page: data?.per_page || 10,
          date_from: data?.date_from || undefined,
          date_to: data?.date_to || undefined,
          order_by: data?.order_by || undefined,
          order_by_direction: data?.order_by_direction || undefined,
        });
        return {
          url: `pt/statistics/vods_paginated?${searchString}`,
        };
      },
      transformResponse: (response: any) => {
        return {
          ...response,
          data: response.data.map((item: any, index: number) => ({
            ...item,
            id: index,
          })),
        };
      },
    }),
    // NOTE: getMessages has been removed - use useCometChatMessages hook instead
    // (server action keeps COMETCHAT_API_KEY secure on server)
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetNoteQuery,
  useGetActivitiesQuery,
  useGetActivitiesSummaryQuery,
  useGetDeadlinesQuery,
  useGetDeadlinesCountersQuery,
  useGetReferralQuery,
  useGetEventQuery,
  useGetStatisticsSummaryQuery,
  useUpdateNoteMutation,
  useUpdateActivityMutation,

  useGetWorkoutCustomersPerDayQuery,
  useGetWorkoutsSummedCustomersQuery,
  useGetWorkoutsSummedCustomersPaginatedQuery,
  useGetWorkoutsSatisfactionTotalsQuery,
  useGetUsersWorkoutsFeedbackListQuery,
  useGetUsersWorkoutsFeedbackPaginatedQuery,

  useGetVodsQuery,
  useGetVodsPaginatedQuery,
} = dashboardApi;
